import React from 'react';
import {getLocalStorage} from "../util/storage";

interface ErrorInfo {
  componentStack: string;
}

interface ErrorData {
  type: string;
  message?: string;
  stack?: string;
  componentStack?: string;
  lineNumber?: string;
  columnNumber?: string;
  filename?: string;
  resourceUrl?: string;
  resourceType?: string;
  [key: string]: any;
}

// 错误上报队列，用于防重复和批量处理
const errorQueue: ErrorData[] = [];
const reportedErrors = new Set<string>();
let isLogging = false;
const originalConsoleError = console.error;
const originalFetch = window.fetch;

const parseErrorStack = (stack?: string) => {
  if (!stack) return { lineNumber: '', columnNumber: '' };
  
  const stackLines = stack.split('\n');

  const errorLine = stackLines[1];
  if (!errorLine) return { lineNumber: '', columnNumber: '' };


  const match = errorLine.match(/:(\d+):(\d+)/);
  if (match) {
    return {
      lineNumber: match[1],
      columnNumber: match[2]
    };
  }
  return { lineNumber: '', columnNumber: '' };
};

// 生成错误唯一标识，用于防重复上报
const getErrorKey = (errorData: ErrorData): string => {
  const key = `${errorData.type}_${errorData.message || ''}_${errorData.filename || ''}_${errorData.lineNumber || ''}_${errorData.columnNumber || ''}`;
  return key.substring(0, 200); // 限制长度
};

// 获取浏览器环境信息
const getBrowserInfo = () => {
  const screen = window.screen || {};
  const viewport = {
    width: window.innerWidth || 0,
    height: window.innerHeight || 0
  };
  
  return {
    screenWidth: screen.width || 0,
    screenHeight: screen.height || 0,
    viewportWidth: viewport.width,
    viewportHeight: viewport.height,
    colorDepth: screen.colorDepth || 0,
    pixelRatio: window.devicePixelRatio || 1,
    language: navigator.language || '',
    platform: navigator.platform || '',
    cookieEnabled: navigator.cookieEnabled || false,
    onLine: navigator.onLine !== undefined ? navigator.onLine : true
  };
};

export const logError = (errorData: ErrorData): void => {
  // 开发环境可以选择性上报（可以通过环境变量控制）
/*  const enableDevReport = process.env.REACT_APP_ENABLE_ERROR_REPORT === 'true';
  if (!enableDevReport && process.env.REACT_APP_ENV === 'dev') {
    originalConsoleError.call(console, 'Dev mode - Error captured but not reported:', errorData);
    return;
  }*/

  // 防重复上报：相同错误在5分钟内只上报一次
  const errorKey = getErrorKey(errorData);
  if (reportedErrors.has(errorKey)) {
    return;
  }
  reportedErrors.add(errorKey);
  // 5分钟后清除记录，允许相同错误再次上报
  setTimeout(() => {
    reportedErrors.delete(errorKey);
  }, 5 * 60 * 1000);

  if (isLogging) {
    // 如果正在上报，将错误加入队列
    errorQueue.push(errorData);
    return;
  }

  isLogging = true;
  
  try {
    const { lineNumber, columnNumber } = parseErrorStack(errorData.stack);
    const userInfo = getLocalStorage('userInfo');
    const timestamp = new Date().toISOString();
    const browserInfo = getBrowserInfo();
    
    // 根据错误类型确定日志级别
    const getLevel = (type: string): string => {
      if (type === 'RUNTIME_ERROR' || type === 'COMPONENT_ERROR' || type === 'PROMISE_REJECTION' || type === 'RESOURCE_ERROR') {
        return 'error';
      }
      if (type === 'CONSOLE_ERROR' || type === 'NETWORK_ERROR') {
        return 'warn';
      }
      return 'info';
    };

    // 构建错误详情对象
    const errorDetails = {
      ...errorData,
      type: errorData.type,
      message: errorData.message || errorData.messages || '',
      stack: errorData.stack || '',
      lineNumber,
      columnNumber,
      pageUrl: window.location.href,
      referrer: document.referrer || '',
      userAgent: navigator.userAgent,
      userInfo,
      browserInfo,
      timestamp,
      // 添加更多上下文信息
      urlParams: window.location.search,
      hash: window.location.hash,
      pathname: window.location.pathname
    };

    // 构建新的日志格式
    const logPayload = {
      timestamp,
      level: getLevel(errorData.type),
      environment: process.env.REACT_APP_ENV || 'prod',
      application: 'moodboard',
      module: errorData.type.toLowerCase().replace(/_/g, '-') || 'unknown',
      content: JSON.stringify(errorDetails)
    };

    originalConsoleError.call(console, 'Captured error:', errorDetails);
    
    // 使用 fetch 发送请求
    const reportUrl = '/krene-logs';
    originalFetch(reportUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(logPayload),
      keepalive: true // 确保在页面关闭时也能发送
    })
    .then(response => {
      if (response.ok) {
        console.log('✅ 错误上报成功');
        // 处理队列中的错误
        processErrorQueue();
      } else {
        console.error('❌ 错误上报失败，状态码:', response.status);
        // 如果失败，尝试使用 sendBeacon 作为备选
        fallbackToSendBeacon(reportUrl, logPayload);
      }
      isLogging = false;
    })
    .catch(err => {
      console.error('❌ 错误上报异常:', err);
      // 如果 fetch 失败，尝试使用 sendBeacon 作为备选
      fallbackToSendBeacon(reportUrl, logPayload);
      isLogging = false;
    });
  } catch (err) {
    originalConsoleError.call(console, 'Error in logError:', err);
    isLogging = false;
  }
};

// 使用 sendBeacon 作为备选方案
const fallbackToSendBeacon = (reportUrl: string, logPayload: any): void => {
  if (navigator.sendBeacon) {
    try {
      const blob = new Blob([JSON.stringify(logPayload)], { type: 'application/json' });
      const sent = navigator.sendBeacon(reportUrl, blob);
      if (sent) {
        console.log('✅ 使用 sendBeacon 上报成功');
      } else {
        console.error('❌ sendBeacon 发送失败');
      }
    } catch (err) {
      originalConsoleError.call(console, 'sendBeacon 异常:', err);
    }
  }
};

// 处理错误队列
const processErrorQueue = (): void => {
  if (errorQueue.length === 0 || isLogging) {
    return;
  }

  const nextError = errorQueue.shift();
  if (nextError) {
    logError(nextError);
  }
};

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logError({
      type: 'COMPONENT_ERROR',
      messages: error.toString(),
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });
  }

  render() {
    return this.props.children;
  }
}

export function initErrorMonitor(): void {
  // 捕获常规JS错误和资源加载错误
  window.addEventListener('error', (event: ErrorEvent) => {
    const target = event.target;
    
    // 检查是否是资源加载错误（图片、脚本、样式表等）
    if (target && target !== window && 'tagName' in target) {
      const element = target as HTMLElement;
      const tagName = element.tagName.toLowerCase();
      let resourceUrl = '';
      
      if (tagName === 'img') {
        resourceUrl = (element as HTMLImageElement).src || '';
      } else if (tagName === 'script') {
        resourceUrl = (element as HTMLScriptElement).src || '';
      } else if (tagName === 'link') {
        resourceUrl = (element as HTMLLinkElement).href || '';
      } else if ('src' in element && (element as any).src) {
        resourceUrl = (element as any).src;
      } else if ('href' in element && (element as any).href) {
        resourceUrl = (element as any).href;
      }
      
      logError({
        type: 'RESOURCE_ERROR',
        message: `资源加载失败: ${tagName}`,
        resourceUrl,
        resourceType: tagName,
        filename: resourceUrl,
        stack: `Failed to load resource: ${resourceUrl}`
      });
      return;
    }

    // 常规JS运行时错误
    logError({
      type: 'RUNTIME_ERROR',
      message: event.message,
      filename: event.filename,
      position: `${event.lineno}:${event.colno}`,
      stack: event.error?.stack,
      lineNumber: event.lineno?.toString(),
      columnNumber: event.colno?.toString()
    });
  }, true); // 使用捕获阶段，确保能捕获资源加载错误

  // 捕获未处理的Promise rejection
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const error = event.reason;
    logError({
      type: 'PROMISE_REJECTION',
      messages: error?.toString(),
      stack: error?.stack || (error instanceof Error ? error.stack : ''),
      message: error instanceof Error ? error.message : String(error)
    });
  });

  // 劫持console.error
  console.error = (...args: any[]): void => {
    if (!isLogging) {
      const error = args[0] instanceof Error ? args[0] : new Error(args[0]?.toString());
      logError({
        type: 'CONSOLE_ERROR',
        messages: args.map(String),
        stack: error.stack,
        message: error.message
      });
    }
    originalConsoleError.apply(console, args);
  };

  // 拦截 fetch 请求错误
  window.fetch = async function(...args: Parameters<typeof fetch>): Promise<Response> {
    try {
      const response = await originalFetch(...args);
      
      // 检查响应状态，4xx和5xx视为错误
      if (!response.ok && response.status >= 400) {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;
        logError({
          type: 'NETWORK_ERROR',
          message: `HTTP ${response.status}: ${response.statusText}`,
          resourceUrl: url,
          resourceType: 'fetch',
          stack: `Failed to fetch: ${url}`
        });
      }
      
      return response;
    } catch (error) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;
      logError({
        type: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : String(error),
        resourceUrl: url,
        resourceType: 'fetch',
        stack: error instanceof Error ? error.stack : ''
      });
      throw error;
    }
  };

  // 捕获 WebSocket 错误（通过代理构造函数）
  const OriginalWebSocket = window.WebSocket;
  (window as any).WebSocket = function(this: WebSocket, url: string | URL, protocols?: string | string[]): WebSocket {
    const ws = new OriginalWebSocket(url, protocols);
    const wsUrl = typeof url === 'string' ? url : url.toString();
    
    const originalOnError = ws.onerror;
    ws.onerror = (event: Event) => {
      logError({
        type: 'WEBSOCKET_ERROR',
        message: 'WebSocket connection error',
        resourceUrl: wsUrl,
        resourceType: 'websocket',
        stack: 'WebSocket error event'
      });
      if (originalOnError) {
        originalOnError.call(ws, event);
      }
    };
    
    return ws;
  };
  
  // 保持 WebSocket 的静态属性
  (window as any).WebSocket.CONNECTING = OriginalWebSocket.CONNECTING;
  (window as any).WebSocket.OPEN = OriginalWebSocket.OPEN;
  (window as any).WebSocket.CLOSING = OriginalWebSocket.CLOSING;
  (window as any).WebSocket.CLOSED = OriginalWebSocket.CLOSED;
  (window as any).WebSocket.prototype = OriginalWebSocket.prototype;
}

export default ErrorBoundary;